import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogIn } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const formatDetail = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong. Please try again.";
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to={location.state?.from || "/admin"} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(form.email.trim(), form.password);
      toast.success("Welcome back, Abhishek.");
      navigate(location.state?.from || "/admin", { replace: true });
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="login-page">
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
        <Lock className="h-6 w-6 text-amber-700" />
      </div>
      <h1 className="mt-6 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-stone-900" data-testid="login-heading">
        Owner sign in
      </h1>
      <p className="mt-3 text-base text-stone-600">This area is private. Sign in to manage posts, projects, testimonials and your CV.</p>

      <form onSubmit={handleSubmit} className="mt-10 space-y-6" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="login-email">Email</Label>
          <Input id="login-email" type="email" required autoComplete="username" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email-input" className="h-11 bg-white" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="login-password">Password</Label>
          <Input id="login-password" type="password" required autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password-input" className="h-11 bg-white" />
        </div>
        {error && (
          <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="login-error">
            {error}
          </p>
        )}
        <Button type="submit" disabled={loading} data-testid="login-submit-button" className="h-11 w-full rounded-full bg-amber-600 font-semibold text-white hover:bg-amber-700 gap-2">
          <LogIn className="h-4 w-4" />
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      </form>
    </div>
  );
}
